import { useCallback, useEffect, useRef, useState, type TransitionEvent } from "react";
import type { Workspace } from "../types/shell.js";

const STORAGE_KEY = "peer-start-sidebar-expanded";
const FADE_OUT_MS = 100;
const RESIZE_MS = 200;
const FADE_IN_MS = 150;

type Phase = "idle" | "fadingOut" | "resizing" | "fadingIn";

function readSavedPreference(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function writeSavedPreference(expanded: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, String(expanded));
  } catch {
    // Storage can be unavailable (private mode); the choice then lasts for this session only.
  }
}

function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function initialExpanded(workspace: Workspace): boolean {
  if (workspace === "employee") return readSavedPreference();
  return false;
}

/**
 * Employee: collapsed unless the user has expanded it. Changing pages does not resize the menu.
 * The manual choice is saved, so it stays until they change it again.
 *
 * Admin and manager: collapsed when the workspace opens. A manual toggle lasts while the user
 * stays in that workspace; it is never saved, so reloading or switching workspace collapses it again.
 */
export function useSidebarExpanded(pathname: string, workspace: Workspace) {
  const isEmployee = workspace === "employee";
  const [saved, setSaved] = useState(readSavedPreference);
  const [expanded, setExpanded] = useState(() => initialExpanded(workspace));
  const previousPath = useRef(pathname);
  const previousWorkspace = useRef(workspace);

  useEffect(() => {
    const workspaceChanged = previousWorkspace.current !== workspace;
    if (!workspaceChanged && previousPath.current === pathname) return;
    previousPath.current = pathname;
    previousWorkspace.current = workspace;
    if (!workspaceChanged) return;
    setExpanded(workspace === "employee" ? saved : false);
  }, [pathname, saved, workspace]);

  const toggle = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    if (!isEmployee) return;
    setSaved(next);
    writeSavedPreference(next);
  }, [expanded, isEmployee]);

  return { expanded, toggle };
}

/**
 * Sequences a width change so labels never visibly move: fade the current labels out,
 * switch layout and animate the width, then fade the new labels in.
 */
export function useSidebarTransition(target: boolean) {
  const [shown, setShown] = useState(target);
  const [phase, setPhase] = useState<Phase>("idle");

  useEffect(() => {
    if (target === shown) {
      setPhase((current) => (current === "fadingOut" ? "fadingIn" : current));
      return;
    }
    if (prefersReducedMotion()) {
      setShown(target);
      setPhase("idle");
      return;
    }
    setPhase("fadingOut");
    const id = window.setTimeout(() => {
      setShown(target);
      setPhase("resizing");
    }, FADE_OUT_MS);
    return () => window.clearTimeout(id);
  }, [target, shown]);

  useEffect(() => {
    if (phase === "resizing") {
      const id = window.setTimeout(() => setPhase("fadingIn"), RESIZE_MS + 80);
      return () => window.clearTimeout(id);
    }
    if (phase === "fadingIn") {
      const id = window.setTimeout(() => setPhase("idle"), FADE_IN_MS);
      return () => window.clearTimeout(id);
    }
  }, [phase]);

  const onTransitionEnd = useCallback((event: TransitionEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || event.propertyName !== "width") return;
    setPhase((current) => (current === "resizing" ? "fadingIn" : current));
  }, []);

  return { shown, labelsVisible: phase === "idle" || phase === "fadingIn", onTransitionEnd };
}

export function fadeClass(visible: boolean): string {
  return `transition-opacity motion-reduce:transition-none ${visible ? "opacity-100 duration-150" : "opacity-0 duration-100"}`;
}
